<script lang="ts">
  import { onMount, tick } from 'svelte';
  import { FUND_STAGES, agentName, currentView, fundLps, fundStageLabel, userEmail } from '../lib/stores';
  import {
    getFundCalendar, getFundCalendarIndex, type FundCalendar, type FundCalendarIndex,
    type FundCalendarIndexMeeting, type FundCalendarMeeting,
  } from '../lib/api/fund-calendar';
  import { addFundNote } from '../lib/api/fund-ir';
  import { canUseAs, isSupportView } from '../lib/supportView';
  import { renderSafeMarkdown } from '../lib/markdown';
  import { localDay } from '../lib/format';
  import MiniMonth from '../lib/calendar/MiniMonth.svelte';
  import {
    addDays, addMonths, coordination, hourRange, initials, isPast, layoutDay, lpName, meetingName,
    meetingStatus, monthGrid, monthStart, overlapGroups, overlapKey, ownerColors, sameDay, weekStart, type CoordinationRow,
  } from '../lib/calendar/week';

  const HOUR_PX = 56;  // change with .cal-hour height
  const SEARCH_WEEKS_BACK = 8;  // of 26, the index route's limit
  const SEARCH_SHOWN = 8;
  const INDEX_TTL = 5 * 60_000;
  const MONTH_CHIPS = 3;
  type Mode = 'week' | 'month' | 'agenda';
  type Index = FundCalendarIndex & { start: Date; end: Date; at: number };

  let anchor = $state(weekStart(new Date()));
  const firstMode = window.matchMedia?.('(max-width: 640px)').matches ? 'agenda' : 'week';
  let mode = $state<Mode>(firstMode);
  let dayMode = $state<'week' | 'agenda'>(firstMode);  // where Month hands back to
  let month = $state(monthStart(new Date()));
  let monthIndex = $state<Index | null>(null);
  let monthError = $state('');
  let pickerOpen = $state(false);
  let pickerMonth = $state(monthStart(new Date()));
  let pickerIndex = $state<Index | null>(null);
  let pickerFailed = $state(false);
  let pickerEl = $state<HTMLElement>();
  let pickedDay = $state<Date | null>(null);
  let data = $state<FundCalendar | null>(null);
  let loading = $state(true);
  let loadError = $state('');
  let people = $state<string[]>([]);  // empty = everyone
  let selectedId = $state<string | null>(null);
  let now = $state(new Date());
  let noteBody = $state('');
  let noteTarget = $state('');
  let noteSaving = $state(false);
  let noteError = $state('');
  let closeButton = $state<HTMLButtonElement>();
  let pageEl = $state<HTMLElement>();
  let query = $state('');
  let searchOpen = $state(false);
  let searchEl = $state<HTMLElement>();
  let searchInput = $state<HTMLInputElement>();
  let index = $state<Index | null>(null);
  let indexError = $state('');
  const indexCache = new Map<string, { at: number; promise: Promise<Index> }>();
  let searchNotice = $state('');
  let opener: HTMLElement | null = null;
  let sequence = 0;
  let demoNotice = $state('');
  // Dev only: the sandbox has no calendar grants, so jojo-ui.test gets a synthetic week.
  const sandboxDemoAvailable = $derived(import.meta.env.DEV && $userEmail === 'ui@jojo-ui.test');

  const viewer = $derived(data?.viewer || monthIndex?.viewer || index?.viewer || '');
  const members = $derived(data?.members || monthIndex?.members || index?.members || []);
  const byPeople = <T extends { team: string[] }>(meeting: T) =>
    !people.length || meeting.team.some(email => people.includes(email));
  const colors = $derived(ownerColors(members, viewer));
  const agent = $derived(($agentName || 'Dealton').split(' ')[0]);
  const visible = $derived((data?.meetings || []).filter(byPeople));
  const groups = $derived(overlapGroups(visible, now));
  const overlapIds = $derived(new Set(groups.flat().map(meeting => meeting.id)));
  const rows = $derived(coordination(visible, now, groups));
  const hours = $derived(hourRange(visible));
  const days = $derived(Array.from({ length: 7 }, (_, i) => addDays(anchor, i))
    .filter((day, i) => i < 5 || visible.some(meeting => sameDay(new Date(meeting.start), day))));
  const selected = $derived(data?.meetings.find(meeting => meeting.id === selectedId) || null);
  const unread = $derived(members.filter(member => member.calendar !== 'connected'));
  const unreadText = $derived(unread.map(member =>
    `${member.email === viewer ? 'Your' : `${name(member.email)}’s`} calendar `
    + (member.calendar === 'not_connected' ? 'isn’t connected.' : 'couldn’t be read just now.')).join(' '));
  const thisWeek = $derived(sameDay(anchor, weekStart(now)));
  const RANGE = new Intl.DateTimeFormat([], { month: 'short', day: 'numeric', year: 'numeric' });
  const rangeLabel = $derived(mode === 'month' ? month.toLocaleDateString([], { month: 'long', year: 'numeric' })
    : RANGE.formatRange(anchor, days[days.length - 1]));
  const monthDays = $derived(monthGrid(month));
  const monthShown = $derived((monthIndex?.meetings || []).filter(byPeople));
  const monthByDay = $derived(monthShown.reduce((byDay, meeting) => {
    const key = localDay(new Date(meeting.start));
    byDay.set(key, [...(byDay.get(key) || []), meeting]);
    return byDay;
  }, new Map<string, FundCalendarIndexMeeting[]>()));
  const inMonth = $derived(monthShown.filter(meeting => new Date(meeting.start).getMonth() === month.getMonth()));
  const shownCount = $derived(mode === 'month' ? inMonth.length : visible.length);
  const lpShown = $derived(new Set((mode === 'month' ? inMonth : visible).flatMap(meeting => meeting.investors.map(lpName))).size);
  const pickerBusy = $derived(pickerIndex
    ? new Set(pickerIndex.meetings.filter(byPeople).map(meeting => localDay(new Date(meeting.start)))) : null);

  // Search runs over a 26-week index in the browser; the week grid stays the source of detail.
  const searchTerms = $derived(query.trim().toLowerCase().split(/\s+/).filter(Boolean));
  const matches = $derived(!index || !searchTerms.length ? [] : index.meetings.filter(meeting => {
    const text = [meeting.summary, ...meeting.team.map(name),
      ...meeting.investors.flatMap(person => [person.display_name, person.institution_name])].join(' ').toLowerCase();
    return searchTerms.every(term => text.includes(term));
  }));
  const upcomingMatches = $derived(matches.filter(meeting => Date.parse(meeting.end) > now.getTime()));
  const earlierMatches = $derived(matches.filter(meeting => Date.parse(meeting.end) <= now.getTime()).reverse());
  const searchRange = $derived(index ? RANGE.formatRange(index.start, addDays(index.end, -1)) : '');
  const searchGaps = $derived((index?.members || []).filter(member => member.calendar !== 'connected'));

  const TIME = new Intl.DateTimeFormat([], { hour: 'numeric', minute: '2-digit' });
  const time = (iso: string) => TIME.format(new Date(iso));
  const span = (meeting: { start: string; end: string }) => TIME.formatRange(new Date(meeting.start), new Date(meeting.end));
  const dayName = (iso: string) => new Date(iso).toLocaleDateString([], { weekday: 'long' });
  const dayLabel = (date: Date) => date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  const shortDate = (iso: string) => new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
  const resultWhen = (iso: string) => new Date(iso).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric',
    ...(new Date(iso).getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) }) + ` · ${time(iso)}`;
  const hourLabel = (hour: number) => new Date(2000, 0, 1, hour).toLocaleTimeString([], { hour: 'numeric' });
  const name = (email: string) => email === viewer ? 'You'
    : (members.find(member => member.email === email)?.name || email.split('@')[0]);
  const hoursFrom = (iso: string, day: Date) => (Date.parse(iso) - day.getTime()) / 3_600_000;
  const stageOf = (meeting: FundCalendarMeeting) =>
    meeting.investors.map(person => person.stage).find(stage => FUND_STAGES.some(item => item.key === stage))
      ?? (meeting.investors.length ? 'Prospect' : undefined);  // an LP with no stage is a Prospect
  const meetingsOn = (day: Date) => visible.filter(meeting => sameDay(new Date(meeting.start), day));

  function blockStyle(meeting: FundCalendarMeeting, day: Date, lane: number, lanes: number) {
    const top = (hoursFrom(meeting.start, day) - hours[0]) * HOUR_PX;
    const bottom = (Math.min(hoursFrom(meeting.end, day), hours[1]) - hours[0]) * HOUR_PX;
    return `top:${top}px;height:${Math.max(bottom - top - 3, 20)}px;`
      + `left:calc(${lane} * 100% / ${lanes} + 3px);width:calc(100% / ${lanes} - 6px);`
      + `--owner:${colors[meeting.lead] || 'var(--border-strong)'}`;
  }

  function rowText(row: CoordinationRow): string {
    const lp = meetingName(row.meeting);
    if (row.kind === 'overlap')
      return `${lp} has ${row.group.length} meetings ${thisWeek ? 'this' : 'that'} week with different leads: `
        + row.group.map(meeting => `${name(meeting.lead)} on ${dayName(meeting.start)}`).join(', ')
        + '. Agree one answer before either call.';
    if (row.kind === 'declined') return `${lp} declined the invite for ${dayName(row.meeting.start)}.`;
    if (row.kind === 'unconfirmed') return `${lp} on ${dayName(row.meeting.start)} hasn't accepted the invite yet.`;
    return `${lp} on ${dayName(row.meeting.start)} has no notes logged.`;
  }
  const ROW_WORD = { overlap: '⚠ Overlap', declined: '✕ Declined', unconfirmed: '◌ Unconfirmed', nonotes: '✎ No notes' };

  async function sandboxDemo(result: FundCalendar | FundCalendarIndex, start: Date, end: Date)
    : Promise<{ calendar: FundCalendar; notice: string } | null> {
    // Keep the import inside this direct compile-time guard: Vite must omit the fixture from builds.
    if (import.meta.env.DEV) {
      const { sandboxDemoCalendar, sandboxDemoNotice } = await import('../lib/calendar/sandbox-demo');
      const you = result.members.find(member => member.email === result.viewer)?.name || 'You';
      return { calendar: sandboxDemoCalendar(result.viewer, you, $fundLps, start, end),
        notice: sandboxDemoNotice };
    }
    return null;
  }

  async function load() {
    const current = ++sequence;
    loading = true;
    try {
      const start = anchor, result = await getFundCalendar(anchor.toISOString(), addDays(anchor, 7).toISOString());
      const demo = sandboxDemoAvailable && !result.meetings.length ? await sandboxDemo(result, start, addDays(start, 7)) : null;
      if (current !== sequence) return;
      data = demo?.calendar || result;
      demoNotice = demo?.notice || '';
      loadError = '';
    } catch (err: any) {
      if (current === sequence) loadError = err?.message || 'Could not read the team calendar.';
    } finally {
      if (current === sequence) loading = false;
    }
  }

  function goWeek(week: Date) {
    anchor = week;
    data = null;
    selectedId = null;
    return load();
  }

  function goMonth(first: Date) {
    month = first;
    monthIndex = null;
    monthError = '';
    const days = monthGrid(first);
    readIndex(days[0], addDays(days[days.length - 1], 1)).then(
      result => { if (sameDay(month, first)) monthIndex = result; },
      (err: any) => { if (sameDay(month, first)) monthError = err?.message || 'Could not read the team calendar.'; });
  }

  /** One step back (-1) or forward (1) in the current view; 0 is Today. */
  function go(step: number) {
    pickedDay = null;
    if (mode === 'month') goMonth(step ? addMonths(month, step) : monthStart(new Date()));
    else goWeek(step ? addDays(anchor, 7 * step) : weekStart(new Date()));
  }

  function setMode(next: Mode) {
    if (next === mode) return;
    const from = mode;
    mode = next;
    pickedDay = null;
    if (next === 'month') {
      goMonth(monthStart(thisWeek ? now : anchor));
      return;
    }
    dayMode = next;
    if (from !== 'month') return;
    // Leaving Month lands on the week holding today, else the month's first day, as Google does.
    const week = weekStart(sameDay(monthStart(now), month) ? now : month);
    if (!data || !sameDay(week, anchor)) goWeek(week);
  }

  /** Week or Agenda for that day, with its column marked and brought into view. */
  async function goToDay(day: Date) {
    if (mode === 'month') mode = dayMode;
    pickedDay = day;
    if (!data || !sameDay(weekStart(day), anchor)) await goWeek(weekStart(day));
    await tick();
    [...pageEl?.querySelectorAll<HTMLElement>('[data-day]') || []].find(node => node.dataset.day === localDay(day))
      ?.scrollIntoView?.({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }

  /** Names and times for a window, shared by search, Month and the date picker for INDEX_TTL. */
  function readIndex(start: Date, end: Date): Promise<Index> {
    const key = `${start.getTime()}-${end.getTime()}`;
    const hit = indexCache.get(key);
    if (hit && Date.now() - hit.at < INDEX_TTL) return hit.promise;
    const promise = (async () => {
      const result = await getFundCalendarIndex(start.toISOString(), end.toISOString());
      const demo = sandboxDemoAvailable && !result.meetings.length ? await sandboxDemo(result, start, end) : null;
      return { ...(demo?.calendar || result), start, end, at: Date.now() };
    })();
    indexCache.set(key, { at: Date.now(), promise });
    promise.catch(() => indexCache.delete(key));
    return promise;
  }

  function togglePicker(open = !pickerOpen) {
    pickerOpen = open;
    if (open) showPickerMonth(mode === 'month' ? month : monthStart(thisWeek ? now : anchor));
  }

  function showPickerMonth(first: Date) {
    pickerMonth = first;
    pickerIndex = null;
    pickerFailed = false;
    const days = monthGrid(first);
    readIndex(days[0], addDays(days[days.length - 1], 1)).then(
      result => { if (sameDay(pickerMonth, first)) pickerIndex = result; },
      () => { if (sameDay(pickerMonth, first)) pickerFailed = true; });
  }

  function pickDay(day: Date) {
    pickerOpen = false;
    if (mode === 'month') goMonth(monthStart(day));
    else goToDay(day);
  }

  const isPicked = (day: Date) => mode === 'month' ? sameDay(monthStart(day), month) : sameDay(weekStart(day), anchor);

  // Google Calendar's keys. Never while typing, with a modifier, or while the drawer is open.
  const SHORTCUTS: Record<string, () => void> = {
    t: () => go(0), j: () => go(1), n: () => go(1), k: () => go(-1), p: () => go(-1),
    w: () => setMode('week'), m: () => setMode('month'), a: () => setMode('agenda'),
    g: () => togglePicker(true), '/': () => searchInput?.focus(),
  };

  function windowKeys(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (selectedId) closeMeeting();
      else if (pickerOpen) pickerOpen = false;
      return;
    }
    const target = event.target;
    if (event.metaKey || event.ctrlKey || event.altKey || selectedId
        || (target instanceof Element && target.closest('input, textarea, select, [contenteditable="true"]'))) return;
    const run = SHORTCUTS[event.key];
    if (!run) return;
    event.preventDefault();
    run();
  }

  function windowClick(event: MouseEvent) {
    const target = event.target as Node;
    if (searchOpen && !searchEl?.contains(target)) searchOpen = false;
    if (pickerOpen && !pickerEl?.contains(target)) pickerOpen = false;
  }

  function togglePerson(email: string | null) {
    if (!email) { people = []; return; }
    if (!people.length) { people = [email]; return; }
    const next = people.includes(email) ? people.filter(item => item !== email) : [...people, email];
    people = next.length === members.length ? [] : next;
  }

  /** `from` gets focus back on close; capture it before any await, when currentTarget is still set. */
  async function openMeeting(id: string, from: EventTarget | null) {
    opener = from as HTMLElement | null;
    selectedId = id;
    noteBody = '';
    noteError = '';
    noteTarget = data?.meetings.find(meeting => meeting.id === id)?.investors[0]?.counterparty_id || '';
    await tick();
    closeButton?.focus({ preventScroll: true });  // a scroll here would cancel showMeeting's
  }

  /** A strip row, search result or month chip acts as a click on its meeting, after bringing it into view.
   *  With no `from`, closing the drawer returns focus to the meeting itself. */
  function showMeeting(id: string, from: EventTarget | null) {
    const block = [...pageEl?.querySelectorAll<HTMLElement>('[data-meeting]') || []].find(node => node.dataset.meeting === id);
    block?.scrollIntoView?.({ block: 'center', inline: 'center', behavior: 'smooth' });
    return openMeeting(id, from || block || null);
  }

  async function openSearch() {
    searchOpen = true;
    searchNotice = '';
    if (index && Date.now() - index.at < INDEX_TTL) return;
    indexError = '';
    const start = addDays(weekStart(new Date()), -7 * SEARCH_WEEKS_BACK);
    try {
      index = await readIndex(start, addDays(start, 7 * 26));
    } catch (err: any) {
      indexError = err?.message || 'Could not search the team calendar.';
    }
  }

  /** Go to the result's week, then open it exactly as a click on the grid would. */
  async function jumpTo(meeting: FundCalendarIndexMeeting) {
    searchOpen = false;
    if (mode === 'month') mode = dayMode;
    pickedDay = null;
    if (!byPeople(meeting)) people = [];
    const week = weekStart(new Date(meeting.start));
    if (!data || !sameDay(week, anchor)) await goWeek(week);
    await tick();
    if (data?.meetings.some(item => item.id === meeting.id)) await showMeeting(meeting.id, null);
    else if (data) {
      searchNotice = 'That meeting is no longer on the team’s calendars.';
      index = null;
      indexCache.clear();
    }
  }

  /** Enter opens the first result, arrows walk the list, Escape closes it and keeps focus in the field. */
  function searchKeys(event: KeyboardEvent) {
    const results = [...(searchEl?.querySelectorAll<HTMLButtonElement>('.cal-res') || [])];
    if (event.key === 'Escape' && searchOpen) {
      event.stopPropagation();
      searchInput?.focus();  // before closing: focusing the field reopens the list
      searchOpen = false;
    } else if (event.key === 'Enter' && event.currentTarget === searchInput) {
      const first = upcomingMatches[0] || earlierMatches[0];
      if (first) { event.preventDefault(); jumpTo(first); }
    } else if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && results.length) {
      event.preventDefault();
      const next = results.indexOf(event.currentTarget as HTMLButtonElement) + (event.key === 'ArrowDown' ? 1 : -1);
      if (next < 0) searchInput?.focus();
      else results[Math.min(next, results.length - 1)].focus();
    }
  }

  function closeMeeting() {
    selectedId = null;
    opener?.focus();
    opener = null;
  }

  async function saveNote(meeting: FundCalendarMeeting) {
    const body = noteBody.trim();
    if (!body || !noteTarget) return;
    if (demoNotice && data) {  // Never write: a real note would trigger a billed briefing refresh.
      const written = new Date().toISOString();
      const note = { note_id: `sandbox-demo-${written}`, counterparty_id: noteTarget, title: 'Meeting notes',
        body, author_email: viewer, created_at: written, updated_at: written };
      data = { ...data, meetings: data.meetings.map(item =>
        item.id === meeting.id ? { ...item, notes: [note, ...item.notes] } : item) };
      noteBody = '';
      return;
    }
    noteSaving = true;
    noteError = '';
    try {
      await addFundNote(noteTarget, `Meeting notes · ${dayLabel(new Date(meeting.start))}`, body);
      noteBody = '';
      await load();
    } catch (err: any) {
      noteError = err?.message || 'Could not save the note.';
    } finally {
      noteSaving = false;
    }
  }

  onMount(() => {
    load();
    const timer = setInterval(() => now = new Date(), 60_000);
    return () => clearInterval(timer);
  });
</script>

<svelte:window onkeydown={windowKeys} onclick={windowClick} />

{#snippet avatar(email: string, small = false)}
  <span class="cal-av" class:sm={small} style:background={colors[email] || 'var(--border-strong)'}
    title={name(email)} aria-hidden="true">{initials(email, members)}</span>
{/snippet}

{#snippet resultGroup(label: string, list: FundCalendarIndexMeeting[])}
  {#if list.length}
    <p class="cal-res-h">{label} · {list.length}</p>
    {#each list.slice(0, SEARCH_SHOWN) as meeting (meeting.id)}
      <button class="cal-res" onclick={() => jumpTo(meeting)} onkeydown={searchKeys}>
        <span class="cal-res-when">{resultWhen(meeting.start)}</span>
        <span class="cal-res-main"><strong>{meetingName(meeting)}</strong><small>{meeting.summary}</small></span>
        <span class="cal-res-lead">{@render avatar(meeting.lead, true)}{name(meeting.lead)}</span>
      </button>
    {/each}
    {#if list.length > SEARCH_SHOWN}<p class="cal-res-note">{list.length - SEARCH_SHOWN} more — add a word to narrow it.</p>{/if}
  {/if}
{/snippet}

{#snippet monthView()}
  <div class="cal-month">
    <div class="cal-mhead" aria-hidden="true">{#each ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as weekday (weekday)}<span>{weekday}</span>{/each}</div>
    <div class="cal-mbody">
      {#each monthDays as day (day.getTime())}
        {@const list = monthByDay.get(localDay(day)) || []}
        {@const today = sameDay(day, now)}
        <div class="cal-mday" class:out={day.getMonth() !== month.getMonth()} class:past={!today && day.getTime() < now.getTime()}>
          <button class="cal-mnum" class:today aria-current={today ? 'date' : undefined}
            aria-label="{dayLabel(day)}{list.length ? `, ${list.length} LP meeting${list.length === 1 ? '' : 's'}` : ''} — open in {dayMode === 'week' ? 'Week' : 'Agenda'}"
            onclick={() => goToDay(day)}>{day.getDate()}</button>
          {#each list.slice(0, MONTH_CHIPS) as meeting (meeting.id)}
            <button class="cal-mchip" class:past={isPast(meeting, now)} style:--owner={colors[meeting.lead] || 'var(--border-strong)'}
              title="{span(meeting)} · {meeting.summary} · {name(meeting.lead)}" onclick={() => jumpTo(meeting)}>
              <span class="cal-mchip-t">{time(meeting.start)}</span> {meetingName(meeting)}</button>
          {/each}
          {#if list.length > MONTH_CHIPS}<button class="cal-mmore" onclick={() => goToDay(day)}>+{list.length - MONTH_CHIPS} more</button>{/if}
        </div>
      {/each}
    </div>
  </div>
  {#if !inMonth.length}<p class="cal-empty">No LP meetings on {people.length ? 'these teammates’' : 'the team’s'} calendars in {rangeLabel}.</p>{/if}
{/snippet}

{#snippet statusWord(meeting: FundCalendarMeeting)}
  {@const info = meetingStatus(meeting, now, overlapIds)}
  <span class="cal-status {info.tone}">{#if info.spark}<span class="cal-spark">✦</span>{' '}{/if}{info.word}</span>
{/snippet}

<div class="cal-page" bind:this={pageEl}>
  <div class="cal-titlerow">
    <h1 class="page-title">Calendar</h1>
    <div class="cal-picker" bind:this={pickerEl}>
      <button class="cal-range" aria-haspopup="dialog" aria-expanded={pickerOpen} title="Go to date (g)"
        onclick={() => togglePicker()}>{rangeLabel}<span class="cal-caret" aria-hidden="true">▾</span></button>
      {#if pickerOpen}
        <div class="cal-picker-pop">
          <MiniMonth month={pickerMonth} today={now} busy={pickerBusy} failed={pickerFailed}
            isSelected={isPicked} onpick={pickDay} onmonth={(delta) => showPickerMonth(addMonths(pickerMonth, delta))} />
        </div>
      {/if}
    </div>
    {#if mode === 'month' ? monthIndex : data}<span class="cal-count">{shownCount} LP meeting{shownCount === 1 ? '' : 's'} · {lpShown} LP{lpShown === 1 ? '' : 's'}</span>{/if}
  </div>

  <div class="cal-toolbar">
    <button class="today-mini-btn" title="Today (t)" onclick={() => go(0)}>Today</button>
    <button class="today-mini-btn cal-icon-btn" aria-label="Previous {mode === 'month' ? 'month' : 'week'}" title="Previous (k)" onclick={() => go(-1)}>‹</button>
    <button class="today-mini-btn cal-icon-btn" aria-label="Next {mode === 'month' ? 'month' : 'week'}" title="Next (j)" onclick={() => go(1)}>›</button>
    <div class="cal-seg" role="group" aria-label="View">
      {#each [['week', 'Week', 'w'], ['month', 'Month', 'm'], ['agenda', 'Agenda', 'a']] as const as [key, label, hotkey] (key)}
        <button class:on={mode === key} aria-pressed={mode === key} title="{label} ({hotkey})" onclick={() => setMode(key)}>{label}</button>
      {/each}
    </div>
    {#if loading && data}<span class="cal-count" role="status">Refreshing…</span>{/if}
    {#if searchNotice}<span class="cal-count" role="status">{searchNotice}</span>{/if}
    <div class="cal-search" bind:this={searchEl} role="search">
      <input type="search" bind:this={searchInput} bind:value={query} placeholder="Search LP meetings…"
        aria-label="Search LP meetings by LP, person or title" onfocus={openSearch} oninput={openSearch}
        onkeydown={searchKeys} />
      {#if searchOpen && searchTerms.length}
        <div class="cal-results">
          {#if !index}
            <p class="cal-res-note" role={indexError ? 'alert' : 'status'}>{indexError || 'Searching the team’s calendars…'}</p>
          {:else}
            {#if !matches.length}<p class="cal-res-note">No LP meetings match “{query.trim()}”.</p>{/if}
            {@render resultGroup('Upcoming', upcomingMatches)}
            {@render resultGroup('Earlier', earlierMatches)}
            <p class="cal-res-foot">Searches {searchRange}.{#each searchGaps as member (member.email)}{' '}{member.email === viewer ? 'Your' : `${name(member.email)}’s`} calendar isn’t included.{/each}</p>
          {/if}
        </div>
      {/if}
    </div>
  </div>

  {#if members.length > 1}
    <div class="cal-filters" role="group" aria-label="People">
      <span class="cal-flabel">People</span>
      <button class="cal-chip" class:on={!people.length} aria-pressed={!people.length} onclick={() => togglePerson(null)}>Everyone</button>
      {#each members as member (member.email)}
        {@const on = !people.length || people.includes(member.email)}
        <button class="cal-chip with-av" class:on aria-pressed={people.includes(member.email)}
          onclick={() => togglePerson(member.email)}>{@render avatar(member.email)}{name(member.email)}</button>
      {/each}
    </div>
  {/if}

  {#if demoNotice}<p class="cal-demo" role="note">{demoNotice}</p>{/if}

  {#if unread.length}
    <p class="cal-unread">
      {unreadText} Their LP meetings can’t appear here.
      {#if unread.some(member => member.email === viewer && member.calendar === 'not_connected')}
        <button class="cal-link" onclick={() => $currentView = 'settings'}>Connect in Settings →</button>
      {/if}
    </p>
  {/if}

  {#if mode === 'month'}
    {#if monthError}
      <div class="today-empty" role="alert">The team calendar is unavailable. {monthError} <button class="today-mini-btn" onclick={() => goMonth(month)}>Retry</button></div>
    {:else if !monthIndex}
      <div class="today-empty" role="status">Reading the team’s calendars…</div>
    {:else}
      {@render monthView()}
    {/if}
  {:else if loadError}
    <div class="today-empty" role="alert">The team calendar is unavailable. {loadError} <button class="today-mini-btn" onclick={load}>Retry</button></div>
  {:else if !data}
    <div class="today-empty" role="status">Reading the team’s calendars…</div>
  {:else}
    {#if rows.length}
      <section class="cal-coord" aria-label="Coordination, {thisWeek ? 'this week' : rangeLabel}">
        <div class="cal-coord-h">{thisWeek ? 'This week' : rangeLabel}</div>
        {#each rows.slice(0, 6) as row (row.kind === 'overlap' ? `overlap:${overlapKey(row.group)}` : `${row.kind}:${row.meeting.id}`)}
          <button class="cal-coord-row" onclick={(event) => showMeeting(row.meeting.id, event.currentTarget)}>
            <span class="cal-word" class:red={row.kind === 'declined'}>{ROW_WORD[row.kind]}</span>
            <span class="cal-coord-text">{rowText(row)}</span>
            <span class="cal-link" aria-hidden="true">Show</span>
          </button>
        {/each}
        {#if rows.length > 6}<div class="cal-coord-more">{rows.length - 6} more in the {mode === 'week' ? 'grid' : 'list'} below.</div>{/if}
      </section>
    {/if}

    {#if mode === 'week'}
      <div class="cal-scroll">
        <div class="cal-grid" style:--days={days.length}>
          <div class="cal-head">
            <div></div>
            {#each days as day (day.getTime())}
              {@const today = sameDay(day, now)}
              <div class="cal-dh" class:today class:picked={!!pickedDay && sameDay(day, pickedDay)}
                class:past={day.getTime() < now.getTime() && !today} data-day={localDay(day)}>
                {day.toLocaleDateString([], { weekday: 'short' })}{#if today}<span class="cal-tlabel">Today</span>{/if}
                <b>{day.getDate()}</b>
              </div>
            {/each}
          </div>
          <div class="cal-body">
            <div class="cal-gutter">
              {#each { length: hours[1] - hours[0] } as _, i}<div class="cal-hour">{i ? hourLabel(hours[0] + i) : ''}</div>{/each}
            </div>
            {#each days as day (day.getTime())}
              {@const today = sameDay(day, now)}
              <div class="cal-col" class:past={day.getTime() < now.getTime() && !today} style:height="{(hours[1] - hours[0]) * HOUR_PX}px">
                {#each layoutDay(meetingsOn(day)) as { item: meeting, lane, lanes } (meeting.id)}
                  {@const height = (Date.parse(meeting.end) - Date.parse(meeting.start)) / 3_600_000 * HOUR_PX}
                  {@const narrow = lanes > 1}
                  <button class="cal-ev" class:narrow class:mine={meeting.team.includes(viewer)} class:past={isPast(meeting, now)}
                    class:sel={selectedId === meeting.id} data-meeting={meeting.id} style={blockStyle(meeting, day, lane, lanes)}
                    aria-label="{time(meeting.start)} {meetingName(meeting)}, led by {name(meeting.lead)}, {meetingStatus(meeting, now, overlapIds).word}"
                    onclick={(event) => openMeeting(meeting.id, event.currentTarget)}>
                    {#if height >= 52 && !narrow}
                      <span class="cal-ev-t">{span(meeting)}</span>
                      <span class="cal-ev-n">{meetingName(meeting)}</span>
                    {:else}
                      <!-- Narrow lanes drop the time: the block's position already says it. -->
                      <span class="cal-ev-n">{#if !narrow}<span class="cal-ev-t">{time(meeting.start)}</span>{' '}{/if}{meetingName(meeting)}</span>
                    {/if}
                    {#if height >= 36}<span class="cal-ev-s">{@render statusWord(meeting)}</span>{/if}
                    {#if !narrow}<span class="cal-ev-avs">{#each meeting.team as email (email)}{@render avatar(email, true)}{/each}</span>{/if}
                  </button>
                {/each}
                {#if today && hoursFrom(now.toISOString(), day) >= hours[0] && hoursFrom(now.toISOString(), day) <= hours[1]}
                  <div class="cal-now" style:top="{(hoursFrom(now.toISOString(), day) - hours[0]) * HOUR_PX}px"><span>{time(now.toISOString())}</span></div>
                {/if}
              </div>
            {/each}
          </div>
        </div>
      </div>
      {#if !visible.length}<p class="cal-empty">No LP meetings on {people.length ? 'these teammates’' : 'the team’s'} calendars this week.</p>{/if}
    {:else}
      <div class="cal-agenda">
        {#each days as day (day.getTime())}
          {@const list = meetingsOn(day).sort((a, b) => Date.parse(a.start) - Date.parse(b.start))}
          <section class="cal-ag-day" class:picked={!!pickedDay && sameDay(day, pickedDay)} data-day={localDay(day)}>
            <header class="cal-ag-dh">
              <h3>{dayLabel(day)}</h3>
              {#if sameDay(day, now)}<span class="status-pill status-pill-grey">Today</span>{/if}
              <span class="cal-count">{list.length} meeting{list.length === 1 ? '' : 's'}</span>
            </header>
            {#each list as meeting (meeting.id)}
              {@const stage = stageOf(meeting)}
              <button class="cal-ag-row" class:past={isPast(meeting, now)} class:sel={selectedId === meeting.id}
                data-meeting={meeting.id} style:--owner={colors[meeting.lead] || 'var(--border-strong)'}
                onclick={(event) => openMeeting(meeting.id, event.currentTarget)}>
                <span class="cal-ag-t">{span(meeting)}</span>
                <span class="cal-ag-n"><strong>{meetingName(meeting)}</strong>{#if stage}<span class="status-pill status-pill-grey">{fundStageLabel(stage)}</span>{/if}<small>{meeting.summary}</small></span>
                {@render statusWord(meeting)}
                <span class="cal-ag-avs">{#each meeting.team as email (email)}{@render avatar(email)}{/each}</span>
              </button>
            {:else}
              <p class="cal-count cal-ag-none">No LP meetings.</p>
            {/each}
          </section>
        {/each}
      </div>
    {/if}
  {/if}
</div>

{#if selected}
  {@const past = isPast(selected, now)}
  {@const info = meetingStatus(selected, now, overlapIds)}
  {@const stage = stageOf(selected)}
  {@const row = rows.find(item => item.meeting.id === selected.id || (item.kind === 'overlap' && item.group.some(meeting => meeting.id === selected.id)))}
  <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
  <div class="cal-scrim" onclick={closeMeeting} aria-hidden="true"></div>
  <div class="cal-drawer" role="dialog" aria-modal="true" aria-labelledby="cal-drawer-title">
    <header class="cal-dr-h">
      <div class="cal-dr-top">
        <span class="cal-when">{dayLabel(new Date(selected.start))} · {span(selected)}{past ? ' · past' : ''}</span>
        <button class="cal-x" bind:this={closeButton} onclick={closeMeeting} aria-label="Close">×</button>
      </div>
      <h2 id="cal-drawer-title">{meetingName(selected)}{#if stage}<span class="status-pill status-pill-grey">{fundStageLabel(stage)}</span>{/if}</h2>
      <p class="cal-dr-sub">{selected.summary}</p>
      <p class="cal-dr-sub">With {selected.investors.map(person => person.display_name).join(', ')}</p>
      <div class="cal-ours">
        <span class="cal-flabel">Ours</span>
        {#each selected.team as email (email)}
          <span class="cal-who">{@render avatar(email, true)}{name(email)}{email === selected.lead ? ' (lead)' : ''}</span>
        {/each}
      </div>
    </header>

    <div class="cal-dr-b">
      {#if row && info.tone}
        <div class="cal-flag"><span class="cal-word" class:red={info.tone === 'red'}>{info.word}</span><span>{rowText(row)}</span></div>
      {/if}

      {#if past}
        <p class="cal-lbl">Notes since the meeting</p>
        {#each selected.notes as note (note.note_id)}
          <article class="cal-note">
            <div class="cal-note-h"><strong>{note.title}</strong><span>{name(note.author_email)} · {shortDate(note.created_at)}</span></div>
            <p>{note.body}</p>
          </article>
        {:else}
          <p class="cal-muted">No notes logged yet.</p>
        {/each}
        <!-- Read-only support view cannot write notes; Use as can (the server enforces both). -->
        {#if !isSupportView() || canUseAs()}
        <label class="cal-lbl" for="cal-note-body">Add notes</label>
        {#if selected.investors.length > 1}
          <select class="cal-select" bind:value={noteTarget} aria-label="Log the note on">
            {#each selected.investors as person (person.counterparty_id)}<option value={person.counterparty_id}>{person.display_name}</option>{/each}
          </select>
        {/if}
        <textarea id="cal-note-body" class="cal-textarea" rows="5" maxlength="10000" bind:value={noteBody}
          placeholder="What was said, what we promised, and what happens next."></textarea>
        {#if noteError}<p class="cal-error" role="alert">{noteError}</p>{/if}
        <div class="cal-actions">
          <button class="btn-primary" onclick={() => saveNote(selected)} disabled={noteSaving || !noteBody.trim()}>{noteSaving ? 'Saving…' : 'Save note'}</button>
        </div>
        <p class="cal-meta">Saved as a team note on the LP. Notes feed its briefings.</p>
        {/if}
      {:else}
        {#each selected.investors as person (person.counterparty_id)}
          <section class="cal-brief">
            <p class="cal-lbl"><span class="cal-spark">✦</span> Call brief{selected.investors.length > 1 ? ` · ${person.display_name}` : ''}</p>
            {#if person.call_brief}
              <div class="md-content">{@html renderSafeMarkdown(person.call_brief.content)}</div>
              <p class="cal-meta">Prepared by {agent} for {name(person.call_brief.prepared_for)} · {shortDate(person.call_brief.generated_at)} · review before relying on it.</p>
              {#if person.call_brief.sources.length}
                <details class="cal-sources"><summary>{person.call_brief.sources.length} source{person.call_brief.sources.length === 1 ? '' : 's'}</summary>
                  <ul>{#each person.call_brief.sources as source (source.id)}<li>{source.label}</li>{/each}</ul>
                </details>
              {/if}
            {:else if person.call_brief_status === 'generating'}
              <p class="cal-muted">{agent} is preparing this brief.</p>
            {:else if person.call_brief_status === 'failed'}
              <p class="cal-muted">This brief couldn’t be prepared. {agent} will try again.</p>
            {:else if person.call_brief_status === 'ready'}
              <p class="cal-muted">A brief is ready. Its text is hidden in this support view.</p>
            {:else}
              <p class="cal-muted">No call brief yet.</p>
            {/if}
          </section>
        {/each}
      {/if}
    </div>

    <footer class="cal-dr-f">
      {#each selected.investors as person (person.counterparty_id)}
        <button class="cal-link" onclick={() => { selectedId = null; $currentView = person.counterparty_id; }}>
          Open {selected.investors.length > 1 ? person.display_name : 'LP page'} →</button>
      {/each}
    </footer>
  </div>
{/if}

<style>
  .cal-page { padding: 4px 0 60px; }
  .cal-titlerow { position: relative; display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
  .cal-titlerow .page-title { margin: 0; }
  .cal-picker { position: relative; }
  .cal-range { gap: 6px; padding: 3px 8px; background: none; border: 1px solid transparent; border-radius: 5px; font-size: 13px; font-weight: 500; color: var(--text-muted); }
  .cal-range:hover, .cal-range[aria-expanded='true'] { background: var(--bg-hover); border-color: transparent; color: var(--text); }
  .cal-caret { font-size: 10px; color: var(--text-dim); }
  .cal-picker-pop { position: absolute; top: calc(100% + 6px); left: 0; z-index: 30; }
  .cal-count { font-family: var(--font-mono); font-size: 11px; color: var(--text-dim); }
  .cal-toolbar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin: 14px 0 12px; }
  .cal-search { position: relative; flex: 0 1 300px; margin-left: auto; }
  .cal-search input { width: 100%; box-sizing: border-box; background: var(--bg-surface); border: 1px solid var(--border); border-radius: 5px; padding: 5px 10px; font: inherit; font-size: 12.5px; color: var(--text); }
  .cal-search input::placeholder { color: var(--text-dim); }
  .cal-search input:focus { outline: none; border-color: var(--border-strong); box-shadow: none; }  /* ambient, like .page-toolbar-search */
  .cal-results { position: absolute; top: calc(100% + 4px); right: 0; z-index: 30; width: min(560px, 92vw); max-height: 60vh; overflow: auto; box-sizing: border-box; padding: 6px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-lg); box-shadow: var(--shadow-md); }
  .cal-res-h { margin: 6px 8px 2px; font-family: var(--font-mono); font-size: 10px; letter-spacing: .11em; text-transform: uppercase; color: var(--text-dim); }
  .cal-res { display: grid; grid-template-columns: 150px minmax(0, 1fr) auto; gap: 10px; align-items: center; width: 100%; padding: 7px 8px; background: none; border: 0; border-radius: 6px; text-align: left; font-size: 12.5px; font-weight: 400; color: var(--text); }
  .cal-res:hover, .cal-res:focus-visible { background: var(--bg-hover); }
  .cal-res-when { font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); }
  .cal-res-main { min-width: 0; }
  .cal-res-main strong { font-weight: 600; }
  .cal-res-main small { display: block; font-size: 11.5px; color: var(--text-dim); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .cal-res-lead { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; color: var(--text-muted); white-space: nowrap; }
  .cal-res-note, .cal-res-foot { margin: 6px 8px; font-size: 12px; color: var(--text-dim); }
  .cal-res-foot { margin-top: 4px; padding-top: 6px; border-top: 1px solid var(--border); }
  .cal-icon-btn { width: 30px; justify-content: center; padding-left: 0; padding-right: 0; }
  .cal-seg { display: inline-flex; border: 1px solid var(--border); border-radius: 5px; overflow: hidden; }
  .cal-seg button { background: none; border: 0; padding: 5px 12px; font: inherit; font-size: 12px; color: var(--text-dim); cursor: pointer; }
  .cal-seg button.on { background: var(--bg-hover); color: var(--text); }

  .cal-filters { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-bottom: 10px; }
  .cal-flabel { font-family: var(--font-mono); font-size: 10px; letter-spacing: .11em; text-transform: uppercase; color: var(--text-dim); margin-right: 4px; }
  .cal-chip { display: inline-flex; align-items: center; gap: 6px; font: inherit; font-size: 12px; color: var(--text-dim); background: none; border: 1px solid var(--border); border-radius: 999px; padding: 3px 10px; cursor: pointer; }
  .cal-chip.with-av { padding-left: 4px; }
  .cal-chip.on { color: var(--text); border-color: var(--border-strong); background: var(--bg-hover); }
  .cal-chip:not(.on) .cal-av { opacity: .45; }
  .cal-unread { font-size: 12px; color: var(--text-dim); margin: 0 0 12px; }
  .cal-demo { font-size: 12px; color: var(--text-muted); border: 1px dashed var(--border-strong); border-radius: var(--radius); padding: 7px 11px; margin: 0 0 12px; }

  .cal-av { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; border-radius: 50%; font-size: 8.5px; font-weight: 700; color: #141019; flex: none; }
  .cal-av.sm { width: 15px; height: 15px; font-size: 7.5px; }
  .cal-spark { color: var(--accent); }
  .cal-link { font: inherit; font-size: 12.5px; font-weight: 600; color: var(--accent); background: none; border: 0; padding: 0; cursor: pointer; white-space: nowrap; }
  .cal-link:hover { text-decoration: underline; text-underline-offset: 3px; }

  .cal-coord { background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 10px 14px; margin-bottom: 14px; }
  .cal-coord-h { font-family: var(--font-mono); font-size: 10px; letter-spacing: .11em; text-transform: uppercase; color: var(--text-dim); margin-bottom: 4px; }
  .cal-coord-row { display: flex; align-items: baseline; gap: 10px; width: 100%; padding: 5px 0; font-size: 13px; font-weight: 400; text-align: left; color: var(--text-muted); background: none; border: 0; border-top: 1px solid var(--border); border-radius: 0; }
  .cal-coord-row:first-of-type { border-top: 0; }
  .cal-coord-row:hover { background: none; border-color: var(--border); }
  .cal-coord-row:hover .cal-link { text-decoration: underline; text-underline-offset: 3px; }
  .cal-coord-text { flex: 1; min-width: 0; }
  .cal-coord-more { font-size: 12px; color: var(--text-dim); padding-top: 4px; }
  .cal-word { font-family: var(--font-mono); font-size: 9.5px; letter-spacing: .4px; text-transform: uppercase; font-weight: 700; white-space: nowrap; flex: none; width: 104px; color: var(--orange-ink); }
  .cal-word.red { color: var(--red-ink); }
  .cal-status { color: var(--text-dim); }
  .cal-status.amber { color: var(--orange-ink); }
  .cal-status.red { color: var(--red-ink); }

  .cal-scroll { overflow-x: auto; }
  .cal-grid { min-width: 620px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-lg); overflow: hidden; }
  .cal-head, .cal-body { display: grid; grid-template-columns: 56px repeat(var(--days, 5), minmax(0, 1fr)); }
  .cal-head { border-bottom: 1px solid var(--border); }
  .cal-dh { padding: 8px 10px; font-size: 12px; color: var(--text-dim); border-left: 1px solid var(--border); }
  .cal-dh b { display: block; font-size: 17px; font-weight: 600; color: var(--text-muted); line-height: 1.2; }
  .cal-dh.today, .cal-dh.today b { color: var(--text); }
  .cal-dh.picked { color: var(--text); box-shadow: inset 0 -2px 0 var(--text-muted); }  /* the day a jump landed on */
  .cal-dh.past { opacity: .7; }
  .cal-tlabel { font-family: var(--font-mono); font-size: 9.5px; letter-spacing: .4px; text-transform: uppercase; color: var(--text); border: 1px solid var(--border-strong); border-radius: 4px; padding: 0 4px; margin-left: 6px; }
  .cal-hour { height: 56px; font-family: var(--font-mono); font-size: 9.5px; color: var(--text-dim); text-align: right; padding-right: 7px; transform: translateY(-6px); white-space: nowrap; }
  .cal-col { position: relative; border-left: 1px solid var(--border); background: repeating-linear-gradient(to bottom, transparent 0, transparent 55px, var(--border) 55px, var(--border) 56px); }
  .cal-col.past { background-color: color-mix(in srgb, var(--text) 3%, transparent); }
  .cal-ev { position: absolute; box-sizing: border-box; border-radius: 7px; background: var(--bg-surface); border: 1px solid var(--border); border-left: 3px solid var(--owner); padding: 4px 7px; text-align: left; overflow: hidden; display: flex; flex-direction: column; align-items: stretch; gap: 1px; font: inherit; color: inherit; cursor: pointer; transition: border-color .1s; }
  .cal-ev:hover { border-color: var(--border-strong); border-left-color: var(--owner); }
  .cal-ev.mine { background: color-mix(in srgb, var(--text) 7%, var(--bg-surface)); }
  .cal-ev.past { opacity: .62; }
  .cal-ev.sel { outline: 1px solid var(--text-muted); z-index: 41; opacity: 1; }  /* above .cal-scrim (40), below the drawer */
  .cal-ev-t { font-family: var(--font-mono); font-size: 9.5px; color: var(--text-dim); }
  .cal-ev-n { padding-right: 34px; font-size: 12px; font-weight: 600; color: var(--text); line-height: 1.25; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .cal-ev-s { font-size: 10.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .cal-ev.narrow .cal-ev-n { padding-right: 0; }
  .cal-ev-avs { position: absolute; right: 5px; bottom: 4px; display: flex; gap: 2px; }
  .cal-now { position: absolute; left: 0; right: 0; height: 0; border-top: 1.5px solid var(--text); z-index: 3; pointer-events: none; }
  .cal-now::before { content: ""; position: absolute; left: -4px; top: -4.5px; width: 7px; height: 7px; border-radius: 50%; background: var(--text); }
  .cal-now span { position: absolute; left: 4px; top: -15px; font-family: var(--font-mono); font-size: 9.5px; color: var(--text); }
  .cal-empty { font-size: 12.5px; color: var(--text-dim); margin: 12px 2px; }

  .cal-ag-day { margin-bottom: 18px; }
  .cal-ag-dh { display: flex; align-items: baseline; gap: 9px; padding-bottom: 7px; margin-bottom: 6px; border-bottom: 1px solid var(--border); }
  .cal-ag-day.picked .cal-ag-dh { border-bottom: 2px solid var(--text-muted); }
  .cal-month { background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-lg); overflow: hidden; }
  .cal-mhead, .cal-mbody { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); }
  .cal-mhead { border-bottom: 1px solid var(--border); }
  .cal-mhead span { padding: 7px 10px; font-size: 12px; color: var(--text-dim); }
  .cal-mday { display: flex; flex-direction: column; gap: 2px; min-width: 0; min-height: 104px; padding: 4px 4px 6px; border-left: 1px solid var(--border); border-top: 1px solid var(--border); }
  .cal-mday:nth-child(7n + 1) { border-left: 0; }
  .cal-mday:nth-child(-n + 7) { border-top: 0; }
  .cal-mday.past { background-color: color-mix(in srgb, var(--text) 3%, transparent); }  /* same tint as .cal-col.past */
  .cal-mday.out .cal-mnum { color: var(--text-dim); opacity: .6; }
  .cal-mnum { align-self: flex-start; justify-content: center; min-width: 24px; height: 24px; padding: 0 5px; background: none; border: 1px solid transparent; border-radius: 12px; font-size: 12px; font-weight: 600; color: var(--text-muted); }
  .cal-mnum:hover { background: var(--bg-hover); border-color: transparent; }
  .cal-mnum.today { border-color: var(--border-strong); color: var(--text); }
  .cal-mchip { display: block; width: 100%; box-sizing: border-box; padding: 2px 6px; background: var(--bg-surface); border: 1px solid var(--border); border-left: 3px solid var(--owner); border-radius: 5px; text-align: left; font-size: 11.5px; font-weight: 600; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .cal-mchip:hover { background: var(--bg-hover); border-color: var(--border-strong); border-left-color: var(--owner); }
  .cal-mchip.past { opacity: .62; }
  .cal-mchip-t { font-family: var(--font-mono); font-size: 10px; font-weight: 400; color: var(--text-dim); }
  .cal-mmore { align-self: flex-start; padding: 1px 6px; background: none; border: 0; font-size: 11px; font-weight: 500; color: var(--text-muted); }
  .cal-mmore:hover { background: var(--bg-hover); }
  .cal-ag-dh h3 { font-size: 15px; font-weight: 500; margin: 0; color: var(--text); }
  .cal-ag-row { display: grid; grid-template-columns: 128px minmax(0, 1fr) auto auto; gap: 14px; align-items: center; width: 100%; box-sizing: border-box; background: var(--bg-surface); border: 1px solid var(--border); border-left: 3px solid var(--owner); border-radius: 10px; padding: 9px 13px; margin-bottom: 6px; text-align: left; font: inherit; font-size: 12px; color: inherit; cursor: pointer; }
  .cal-ag-row:hover { background: var(--bg-hover); }
  .cal-ag-row.past { opacity: .62; }
  .cal-ag-row.sel { position: relative; outline: 1px solid var(--text-muted); z-index: 41; opacity: 1; }  /* same as .cal-ev.sel */
  .cal-ag-t { font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); }
  .cal-ag-n { min-width: 0; }
  .cal-ag-n strong { font-size: 13.5px; font-weight: 600; color: var(--text); }
  .cal-ag-n small { display: block; font-size: 11.5px; color: var(--text-dim); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .cal-ag-avs { display: flex; gap: 3px; }
  .cal-ag-none { padding: 6px 2px; margin: 0; }
  .status-pill { margin-left: 8px; vertical-align: 2px; }

  .cal-scrim { position: fixed; inset: 0; background: rgba(0, 0, 0, .35); z-index: 40; }
  .cal-drawer { position: fixed; top: 0; right: 0; bottom: 0; width: 460px; max-width: 100%; background: var(--bg-surface); border-left: 1px solid var(--border); box-shadow: var(--shadow-md); z-index: 41; display: flex; flex-direction: column; }
  .cal-dr-h { padding: 16px 18px 12px; border-bottom: 1px solid var(--border); }
  .cal-dr-top { display: flex; align-items: center; gap: 8px; }
  .cal-when { font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); }
  .cal-x { margin-left: auto; background: none; border: 0; color: var(--text-dim); font-size: 18px; line-height: 1; padding: 2px 4px; cursor: pointer; }
  .cal-x:hover { color: var(--text); }
  .cal-drawer h2 { font-size: 18px; font-weight: 650; margin: 6px 0 2px; color: var(--text); overflow-wrap: anywhere; }
  .cal-dr-sub { font-size: 12.5px; color: var(--text-muted); margin: 2px 0 0; overflow-wrap: anywhere; }
  .cal-ours { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-top: 8px; font-size: 12px; color: var(--text-muted); }
  .cal-who { display: inline-flex; align-items: center; gap: 5px; margin-right: 6px; }
  .cal-dr-b { padding: 14px 18px; overflow-y: auto; flex: 1; }
  .cal-flag { display: flex; gap: 9px; align-items: baseline; font-size: 12.5px; color: var(--text-muted); background: var(--bg-hover); border-radius: var(--radius); padding: 8px 11px; margin-bottom: 14px; }
  .cal-flag .cal-word { width: auto; }
  .cal-lbl { display: block; font-family: var(--font-mono); font-size: 10px; letter-spacing: .11em; text-transform: uppercase; color: var(--text-dim); margin: 0 0 8px; }
  .cal-brief :global(.md-content h2) { font-size: 14px; margin: 14px 0 6px; }
  .cal-brief + .cal-brief { margin-top: 18px; padding-top: 14px; border-top: 1px solid var(--border); }
  .cal-muted { font-size: 12.5px; color: var(--text-dim); font-style: italic; margin: 0 0 12px; }
  .cal-meta { font-size: 11.5px; color: var(--text-dim); margin: 10px 0 0; }
  .cal-sources { font-size: 12px; color: var(--text-dim); margin-top: 8px; }
  .cal-sources summary { cursor: pointer; }
  .cal-sources ul { margin: 6px 0 0; padding-left: 18px; }
  .cal-note { background: var(--bg-hover); border-radius: var(--radius); padding: 9px 11px; margin-bottom: 8px; font-size: 12.5px; }
  .cal-note-h { display: flex; justify-content: space-between; gap: 10px; color: var(--text); }
  .cal-note-h span { color: var(--text-dim); font-size: 11.5px; white-space: nowrap; }
  .cal-note p { margin: 4px 0 0; color: var(--text-muted); white-space: pre-wrap; overflow-wrap: anywhere; }
  .cal-note + .cal-lbl, .cal-muted + .cal-lbl { margin-top: 16px; }
  .cal-select, .cal-textarea { width: 100%; box-sizing: border-box; font: inherit; font-size: 13px; color: var(--text); background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius); padding: 8px 10px; margin-bottom: 8px; }
  .cal-textarea { resize: vertical; min-height: 96px; }
  .cal-textarea:focus, .cal-select:focus { outline: none; border-color: color-mix(in srgb, var(--accent) 55%, transparent); }
  .cal-error { font-size: 12px; color: var(--red-ink); margin: 0 0 8px; }
  .cal-actions { display: flex; gap: 8px; }
  .cal-dr-f { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; padding: 12px 18px; border-top: 1px solid var(--border); }

  @media (max-width: 640px) {
    .cal-ag-row { grid-template-columns: minmax(0, 1fr) auto; }
    .cal-ag-t { grid-column: 1 / -1; }
    .cal-word { width: auto; }
    .cal-coord-row { flex-wrap: wrap; }
    .cal-search { flex-basis: 100%; margin-left: 0; }
    .cal-results { left: 0; right: auto; width: 100%; }
    .cal-res { grid-template-columns: minmax(0, 1fr) auto; }
    .cal-res-when { grid-column: 1 / -1; }
    .cal-mday { min-height: 64px; padding: 3px 2px; }
    .cal-mhead span { padding: 6px 4px; font-size: 11px; }
    .cal-mchip { height: 6px; padding: 0; border: 0; border-radius: 3px; background: var(--owner); font-size: 0; }  /* a bar per meeting; the name stays as its label */
    .cal-mchip-t { font-size: 0; }
    .cal-picker { position: static; }  /* anchor to .cal-titlerow so the picker opens from the left edge */
    .cal-mmore { font-size: 10px; padding: 0 2px; }
  }
</style>
